/**
 * useDashboardWidgets — Stream D (Paritaet R4)
 *
 * Server-Anbindung der neuen Dashboard-Endpunkte:
 *   GET   /api/v2/dashboard/widgets          -> Katalog + gespeichertes Layout + Plan-Meta
 *   GET   /api/v2/dashboard/widget_values    -> Werte je Widget-ID (ein Request fuer alle)
 *   PATCH /api/v2/dashboard/layout           -> Layout speichern (Premium-Gate)
 *   GET   /api/v2/dashboard/timespan_limits  -> Plan-Meta alleine (Datepicker-Grenzen)
 *
 * Die vier Pfade liegen (noch) nicht im installierten @heliumtracker/api-types-Paket
 * — daher gezielte Casts an genau diesen Aufrufstellen, gleiches Muster wie
 * useCryptoPrices.ts und useSubscriptionHistory.ts. Kein globales `as any` auf Daten:
 * die Antworten werden ueber explizite Normalisierer in die Typen unten ueberfuehrt.
 */
import { useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import apiClient from '../../lib/api-client'
import { ApiError } from '../../lib/errors/ApiError'
import { apiErrorCode, apiErrorStatus } from '../../lib/errors/apiErrorMessage'
import { useAuthStore } from '../../stores/authStore'
import {
  isScopeHiddenWidgetId,
  normalizeScope,
  tagScopeKey,
  tagScopeQuery,
  type DashboardScope,
} from './dashboardScope'
import {
  defaultLayoutEntries,
  migrateLayoutEntries,
  isWidgetId,
  resolveWidgetId,
  resolveWidgetIds,
  type LayoutEntry,
  type WidgetId,
} from './widgetCatalog'

export const DASHBOARD_WIDGETS_QUERY_KEY = ['dashboard', 'widgets'] as const
export const DASHBOARD_TIMESPAN_LIMITS_QUERY_KEY = ['dashboard', 'timespan_limits'] as const

// ---------------------------------------------------------------------------
// Typen (1:1 zum BE-Contract)
// ---------------------------------------------------------------------------

export type PlanMeta = {
  is_premium: boolean
  /** Darf der User das Layout speichern? (Free: false -> Upsell + Save disabled) */
  customizable: boolean
  reward_days: number
  /** Alias von reward_days — die Tages-Obergrenze des Tarifs fuer den Upsell. */
  max_days: number
  /** Obergrenze des besten Tarifs (180) — Upsell nur wenn reward_days < max_reward_days. */
  max_reward_days: number
  /** Fruehestes waehlbares Datum als "YYYY-MM-DD" (Datepicker-min). */
  min_start_date: string
  upgrade_url: string
}

export type WidgetTemplate = 'rewards' | 'average' | 'payout' | 'balance' | 'hotspots' | 'v2'

export type WidgetDefinition = {
  id: WidgetId
  name: string
  /** Bereits lokalisiertes Label vom BE — das FE bevorzugt trotzdem seinen i18n-Key. */
  label: string
  template: WidgetTemplate | string
  duration_amount: number | null
  duration_offset: number
  duration_type: 'days' | 'months' | null
  color_light: string | null
  color_dark: string | null
  box_number: number
  default: boolean
  source: 'v1' | 'v2'
}

export type WidgetComparison = {
  total_usd: number
  rewards_usd: number
  start_time: string | null
  end_time: string | null
}

/**
 * Ein Wert-Objekt fuer alle Templates. Felder sind bewusst optional, weil das
 * BE je nach `template` unterschiedliche Teilmengen liefert (rewards/average,
 * payout, balance, hotspots).
 */
export type WidgetValue = {
  id: WidgetId
  name?: string
  template: WidgetTemplate | string
  duration_amount?: number | null
  duration_offset?: number
  duration_type?: 'days' | 'months' | null
  color_light?: string | null
  color_dark?: string | null
  start_time?: string | null
  end_time?: string | null

  // rewards / average
  total_usd?: number
  rewards_usd?: number
  commissions?: number
  affiliate?: number
  hnt?: number
  iot?: number
  mobile?: number
  compare?: WidgetComparison | null
  /** null wenn die Vorperiode 0 war — dann KEIN Badge (v1-Paritaet). */
  percent_change?: number | null
  /** Nur bei laufender Periode (duration_amount == 1 && duration_offset == 0). */
  prediction_usd?: number | null

  // average
  per_hotspot?: boolean
  num_hotspots?: number
  total_usd_per_hotspot_per_day?: number

  // payout (since_payout, HNT-only)
  has_payout?: boolean
  last_payout_id?: number | null
  last_payout_end_date?: string | null
  total_hnt?: number
  rewards_hnt?: number
  commissions_hnt?: number
  affiliate_hnt?: number

  // balance
  balance_hnt?: number
  balance_iot?: number
  balance_mobile?: number
  balance_usd?: number

  // hotspots
  num_wallets?: number
}

export const DEFAULT_PLAN_META: PlanMeta = {
  is_premium: false,
  customizable: false,
  reward_days: 14,
  max_days: 14,
  max_reward_days: 180,
  min_start_date: '',
  upgrade_url: '/plans',
}

/** Fehlercodes von PATCH /api/v2/dashboard/layout. */
export type LayoutErrorCode = 'premium_required' | 'invalid_widgets' | 'save_failed' | 'unknown'

export class DashboardLayoutError extends Error {
  constructor(
    public readonly code: LayoutErrorCode,
    public readonly upgradeUrl: string | null = null,
  ) {
    super(`dashboard layout save failed: ${code}`)
    this.name = 'DashboardLayoutError'
  }
}

// ---------------------------------------------------------------------------
// Normalisierer
// ---------------------------------------------------------------------------

function num(value: unknown, fallback = 0): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback
}

export function normalizeMeta(raw: unknown): PlanMeta {
  const m = (raw ?? {}) as Partial<PlanMeta>
  const rewardDays = num(m.reward_days, DEFAULT_PLAN_META.reward_days)
  return {
    is_premium: m.is_premium === true,
    customizable: m.customizable === true,
    reward_days: rewardDays,
    max_days: num(m.max_days, rewardDays),
    max_reward_days: num(m.max_reward_days, DEFAULT_PLAN_META.max_reward_days),
    min_start_date: typeof m.min_start_date === 'string' ? m.min_start_date : '',
    upgrade_url: typeof m.upgrade_url === 'string' ? m.upgrade_url : DEFAULT_PLAN_META.upgrade_url,
  }
}

function normalizeDefinitions(raw: unknown): WidgetDefinition[] {
  if (!Array.isArray(raw)) return []
  const out: WidgetDefinition[] = []
  const seen = new Set<WidgetId>()
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue
    const d = item as Partial<WidgetDefinition>
    // Unbekannte Server-IDs tolerant ignorieren, Legacy-Alias 'graph' auf
    // 'rewards_chart' heben und die daraus entstehende Dublette verwerfen (F7).
    const id = resolveWidgetId(d.id)
    if (id === null || seen.has(id)) continue
    seen.add(id)
    out.push({
      id,
      name: typeof d.name === 'string' ? d.name : id,
      label: typeof d.label === 'string' ? d.label : (d.name ?? id),
      template: typeof d.template === 'string' ? d.template : 'rewards',
      duration_amount: typeof d.duration_amount === 'number' ? d.duration_amount : null,
      duration_offset: num(d.duration_offset),
      duration_type: d.duration_type === 'days' || d.duration_type === 'months' ? d.duration_type : null,
      color_light: typeof d.color_light === 'string' ? d.color_light : null,
      color_dark: typeof d.color_dark === 'string' ? d.color_dark : null,
      box_number: num(d.box_number),
      default: d.default === true,
      source: d.source === 'v2' ? 'v2' : 'v1',
    })
  }
  return out
}

function normalizeValues(raw: unknown): WidgetValue[] {
  if (!Array.isArray(raw)) return []
  return raw.filter((v): v is WidgetValue => {
    if (!v || typeof v !== 'object') return false
    return isWidgetId((v as { id?: unknown }).id)
  })
}

function normalizeEffective(raw: unknown, fallback: LayoutEntry[]): WidgetId[] {
  // resolveWidgetIds hebt Legacy-Aliase ('graph' -> 'rewards_chart') und
  // dedupliziert — sonst rendert das Dashboard denselben Chart zweimal (F7).
  const ids = resolveWidgetIds(raw)
  if (ids.length > 0) return ids
  return fallback.filter((e) => e.visible).map((e) => e.id)
}

// ---------------------------------------------------------------------------
// Untypisierte Aufrufhilfen (Pfade noch nicht im api-types-Paket)
// ---------------------------------------------------------------------------

type UntypedResult = { data?: unknown; error?: unknown; response?: Response }
type UntypedGet = (url: string, init?: object) => Promise<UntypedResult>
type UntypedPatch = (url: string, init?: object) => Promise<UntypedResult>

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

const EMPTY_DEFINITIONS: WidgetDefinition[] = []
const EMPTY_HIDDEN_IDS: string[] = []
const FALLBACK_LAYOUT: LayoutEntry[] = defaultLayoutEntries()
const FALLBACK_EFFECTIVE: WidgetId[] = FALLBACK_LAYOUT.filter((e) => e.visible).map((e) => e.id)

export type DashboardWidgetCatalog = {
  widgets: WidgetDefinition[]
  layout: LayoutEntry[]
  effective: WidgetId[]
  meta: PlanMeta
  isLoading: boolean
  isError: boolean
}

/** GET /api/v2/dashboard/widgets — Katalog + gespeichertes Layout + Plan-Meta. */
export function useDashboardWidgetCatalog(): DashboardWidgetCatalog {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)

  const { data, isLoading, isError } = useQuery({
    queryKey: DASHBOARD_WIDGETS_QUERY_KEY,
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000,
    retry: 1,
    queryFn: async () => {
      const res = await (apiClient.GET as unknown as UntypedGet)('/api/v2/dashboard/widgets', {})
      if (res.error) throw new Error('dashboard widgets fetch failed')
      const body = (res.data ?? {}) as Record<string, unknown>
      const layout = migrateLayoutEntries((body.layout as { widgets?: unknown } | undefined)?.widgets)
      return {
        widgets: normalizeDefinitions(body.widgets),
        layout,
        effective: normalizeEffective(body.effective, layout),
        meta: normalizeMeta(body.meta),
      }
    },
  })

  // Stabile Fallback-Referenzen: useDashboardLayout vergleicht `layout` per
  // Identitaet (adjust-during-render). Ein bei jedem Render neu gebautes
  // Default-Array wuerde dort eine Render-Schleife ausloesen.
  return {
    widgets: data?.widgets ?? EMPTY_DEFINITIONS,
    layout: data?.layout ?? FALLBACK_LAYOUT,
    effective: data?.effective ?? FALLBACK_EFFECTIVE,
    meta: data?.meta ?? DEFAULT_PLAN_META,
    isLoading,
    isError,
  }
}

export type WidgetValuesOptions = {
  from?: string
  to?: string
  enabled?: boolean
  /**
   * R5/Stream TD: Tag-Scope (`/dashboard?tag=<id>` -> `tag_id`). Der Wert gehoert
   * zwingend auch in den Query-Key, sonst liefert der Cache beim Scope-Wechsel
   * die Account-Werte.
   */
  tagId?: number | null
}

export type DashboardWidgetValues = {
  /** Werte nach Widget-ID — Nachschlagen ohne Array-Suche in jeder Karte. */
  values: Partial<Record<WidgetId, WidgetValue>>
  unknownIds: string[]
  /**
   * Vom BE im Scope bewusst ausgeblendete Widgets (v1 @hide_balance:
   * `balance` + `since_payout`). Leer ausserhalb des Tag-Scopes.
   */
  hiddenIds: string[]
  /** Additiver `scope`-Block, `null` im Account-Dashboard. */
  scope: DashboardScope | null
  meta: PlanMeta
  isLoading: boolean
  isError: boolean
  refetch: () => void
}

/**
 * GET /api/v2/dashboard/widget_values — EIN Request fuer alle sichtbaren IDs.
 * `ids` leer => Hook bleibt inaktiv (kein Request ohne sichtbare Kachel).
 */
export function useDashboardWidgetValues(
  ids: WidgetId[],
  options: WidgetValuesOptions = {},
): DashboardWidgetValues {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const idsKey = [...ids].sort().join(',')
  const { from, to, enabled = true, tagId = null } = options

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: [
      'dashboard',
      'widget_values',
      idsKey,
      from ?? null,
      to ?? null,
      tagScopeKey(tagId),
    ],
    enabled: isAuthenticated && enabled && ids.length > 0,
    staleTime: 60 * 1000,
    retry: 1,
    queryFn: async () => {
      const query: Record<string, string> = { ids: ids.join(','), ...tagScopeQuery(tagId) }
      if (from) query.from = from
      if (to) query.to = to
      const res = await (apiClient.GET as unknown as UntypedGet)('/api/v2/dashboard/widget_values', {
        params: { query },
      })
      if (res.error) throw new Error('dashboard widget values fetch failed')
      const body = (res.data ?? {}) as Record<string, unknown>
      const list = normalizeValues(body.values)
      const map: Partial<Record<WidgetId, WidgetValue>> = {}
      for (const v of list) map[v.id] = v
      return {
        values: map,
        unknownIds: Array.isArray(body.unknown_ids)
          ? body.unknown_ids.filter((x): x is string => typeof x === 'string')
          : [],
        hiddenIds: Array.isArray(body.hidden_ids)
          ? body.hidden_ids.filter((x): x is string => typeof x === 'string')
          : [],
        scope: normalizeScope(body.scope),
        meta: normalizeMeta(body.meta),
      }
    },
  })

  return {
    values: data?.values ?? {},
    unknownIds: data?.unknownIds ?? [],
    hiddenIds: data?.hiddenIds ?? EMPTY_HIDDEN_IDS,
    scope: data?.scope ?? null,
    meta: data?.meta ?? DEFAULT_PLAN_META,
    isLoading,
    isError,
    refetch: () => void refetch(),
  }
}

/** GET /api/v2/dashboard/timespan_limits — Plan-Meta fuer Picker/Upsell. */
export function useTimespanLimits(): { meta: PlanMeta; isLoading: boolean } {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)

  const { data, isLoading } = useQuery({
    queryKey: DASHBOARD_TIMESPAN_LIMITS_QUERY_KEY,
    enabled: isAuthenticated,
    staleTime: 10 * 60 * 1000,
    retry: 1,
    queryFn: async () => {
      const res = await (apiClient.GET as unknown as UntypedGet)(
        '/api/v2/dashboard/timespan_limits',
        {},
      )
      if (res.error) throw new Error('timespan limits fetch failed')
      return normalizeMeta(res.data)
    },
  })

  return { meta: data ?? DEFAULT_PLAN_META, isLoading }
}

/** Body eines Fehlers — ApiError legt ihn unter `.body` ab, roh geworfene Objekte sind der Body. */
function errorBody(error: unknown): { error?: { code?: string; upgrade_url?: string } } {
  if (error instanceof ApiError) {
    return (error.body ?? {}) as { error?: { code?: string; upgrade_url?: string } }
  }
  return (error ?? {}) as { error?: { code?: string; upgrade_url?: string } }
}

/**
 * R4-Review F2: `apiClient` WIRFT bei HTTP >= 400 eine `ApiError` — der frueher
 * ausgewertete `res.error`-Zweig wurde nie erreicht, weshalb 403
 * premium_required und 422 invalid_widgets im UI als generischer Fehler
 * ankamen. Der Code kommt jetzt aus dem zentralen `apiErrorCode`-Helper
 * (versteht ApiError, `{ body }` und rohen Body), der Status dient als Fallback.
 */
export function layoutErrorFrom(error: unknown): DashboardLayoutError {
  const upgradeUrl = errorBody(error).error?.upgrade_url ?? null
  const code = apiErrorCode(error)
  if (code === 'premium_required' || code === 'invalid_widgets' || code === 'save_failed') {
    return new DashboardLayoutError(code, upgradeUrl)
  }

  const status = apiErrorStatus(error)
  if (status === 403) return new DashboardLayoutError('premium_required', upgradeUrl)
  if (status === 422 || status === 400) return new DashboardLayoutError('invalid_widgets', upgradeUrl)
  if (status !== undefined && status >= 500) return new DashboardLayoutError('save_failed', upgradeUrl)

  return new DashboardLayoutError('unknown', upgradeUrl)
}

/**
 * PATCH /api/v2/dashboard/layout — Reihenfolge des Arrays = Anzeigereihenfolge.
 * Leeres Array setzt serverseitig auf das Default-Set zurueck.
 */
export function useSaveDashboardLayout() {
  const queryClient = useQueryClient()

  return useMutation<LayoutEntry[], DashboardLayoutError, LayoutEntry[]>({
    mutationFn: async (widgets: LayoutEntry[]) => {
      let res: UntypedResult
      try {
        res = await (apiClient.PATCH as unknown as UntypedPatch)('/api/v2/dashboard/layout', {
          body: { widgets },
        })
      } catch (err) {
        throw layoutErrorFrom(err)
      }
      // Defensive: ein Client, der statt zu werfen `{ error }` zurueckgibt.
      if (res.error) throw layoutErrorFrom(res.error)
      const body = (res.data ?? {}) as Record<string, unknown>
      return migrateLayoutEntries((body.layout as { widgets?: unknown } | undefined)?.widgets)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DASHBOARD_WIDGETS_QUERY_KEY })
    },
  })
}

/**
 * Bequemer Zugriff auf die IDs, fuer die Werte geladen werden sollen:
 * alle sichtbaren Layout-Eintraege, die ein Wert-Template haben.
 */
const VALUE_BACKED_IDS = new Set<WidgetId>([
  'rewards_7d',
  'avg_7d',
  'avg_30d',
  'rewards_this_month',
  'rewards_yesterday',
  'rewards_30d',
  'balance',
  'rewards_last_month',
  'hotspots',
  'since_payout',
  'rewards_two_days_ago',
])

export type ValueBackedIdsOptions = {
  /**
   * R5/Stream TD: Im Tag-Scope blendet das BE `balance` und `since_payout` aus
   * (v1 @hide_balance). Das FE fragt sie dann gar nicht erst an — spart einen
   * Roundtrip und haelt FE/BE konsistent.
   */
  tagScoped?: boolean
}

export function useValueBackedIds(
  visibleIds: WidgetId[],
  options: ValueBackedIdsOptions = {},
): WidgetId[] {
  const tagScoped = options.tagScoped === true
  return useMemo(
    () =>
      visibleIds.filter(
        (id) => VALUE_BACKED_IDS.has(id) && !(tagScoped && isScopeHiddenWidgetId(id)),
      ),
    [visibleIds, tagScoped],
  )
}

export { VALUE_BACKED_IDS }
